import { safeFetch } from '@/services/fetch-api/fetch.api'

/**
 * Plucks address components from the google maps geocoding data format
 * by their "type" values
 *
 * @param {object} components The address_components node from a google maps geocoding response.
 * @returns {object} Address components reformatted to be consumed by building/add-building sagas.
 */
const extractAddressComponentsFromGoogleGeocodingData = (components) => {
	const getComponent = (type) => {
		const found = components.find((c) => c.types.includes(type))
		return found ? found.long_name : undefined
	}

	const streetNumber = getComponent('street_number')
	const route = getComponent('route')
	const address =
		streetNumber && route
			? `${getComponent('street_number')} ${getComponent('route')}`
			: ''

	return {
		address,
		city:
			// this succession of types is based on observed google maps data structure for north american addresses
			getComponent('locality') ||
			getComponent('administrative_area_level_3') ||
			getComponent('administrative_area_level_2') ||
			'',
		state: getComponent('administrative_area_level_1') || '',
		zipCode: getComponent('postal_code') || '',
		country: getComponent('country') || '',
	}
}

/**
 * Transforms a google maps suggestions "placePrediction" node into a format compatible with the fields we were consuming from bing maps.
 *
 * @param {object} placePrediction A "placePrediction" node from a google maps suggestions response. Tries to return a "place name" if present, otherwise uses street address.
 * @returns {object} Address pieces reformatted for use by search component.
 */
export const transformMapsSuggestionsForSearch = ({ placePrediction }) => {
	const namedTypes = new Set([
		'point_of_interest',
		'establishment',
		'tourist_attraction',
	])

	const {
		structuredFormat: { mainText, secondaryText },
		types,
	} = placePrediction

	const isNamedPlace = types.some((t) => namedTypes.has(t))
	const formattedAddress = `${mainText.text}, ${secondaryText.text}`

	return {
		...(isNamedPlace && { name: `${mainText.text}, ${secondaryText.text}` }),
		address: {
			formattedAddress,
		},
	}
}

/**
 * Gets an array of place suggestions from Google Maps for a user-entered string.
 * Fetch error is left uncaught to match bing utils behaviour.
 *
 * @param {string} userInput Text entered by the user to find suggestions for
 * @returns {Promise<object[]>} Place suggestions based on the user's input text, or an empty array if suggestions can't be fetched
 */
export const getMapsSuggestions = async (userInput) => {
	const res = await safeFetch('addressSuggestion', { textQuery: userInput })
	const suggestions =
		res?.data?.suggestions?.length > 0 ? res.data.suggestions : []
	return suggestions.map(transformMapsSuggestionsForSearch)
}

/**
 * Geocodes a provided address string from the orchestration server, which calls google maps.
 *
 * @param  {string} address Formatted display address from Google Maps
 * @returns {Promise<object|null>} Object containing location details includeing latitude and longitude, or null if address cannot be geocoded.
 */
export const geocodeAddress = async (address) => {
	try {
		const res = await safeFetch('addressGeocode', { address })
		const googleResult = res.data

		if (googleResult.results.length > 0) {
			const [targetResult] = googleResult.results
			const addressComponents = extractAddressComponentsFromGoogleGeocodingData(
				targetResult.address_components,
			)

			let coordinates
			if (targetResult.geometry.location) {
				coordinates = {
					latitude: targetResult.geometry.location.lat,
					longitude: targetResult.geometry.location.lng,
				}
			}

			const result = {
				...addressComponents,
				...coordinates,
				fullAddress: targetResult.formatted_address,
			}

			return result
		}
		return null
	} catch {
		// TODO Notify?
		return null
	}
}
